import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { getMyTechIdentity } from "./api";
import { hasRole } from "./auth";

// sessionStorage, not localStorage: impersonation should end with the browser
// session. An admin who closes the tab and comes back is themselves again.
const IMP_KEY = "qaPortal.impersonatedTech";

const Ctx = createContext({
    techWebId: null,
    loading: true,
    impersonated: null,
    effectiveWebId: null,
    viewingAsTech: false,
    viewingAsIP: false,
    effectiveReaderId: null,
    canImpersonate: false,
    startImpersonation: () => {},
    stopImpersonation: () => {},
});

function readStored() {
    try {
        const raw = sessionStorage.getItem(IMP_KEY);
        if (!raw) return null;
        const v = JSON.parse(raw);
        if (!v) return null;
        // kind was added when IPs became impersonable; anything stored before
        // that is a tech, which is what the entry meant at the time.
        if (!v.kind && v.techWebId) return { ...v, kind: "tech" };
        if (v.kind === "tech" && v.techWebId) return v;
        if (v.kind === "ip" && v.readerId) return v;
        return null;
    } catch {
        return null;
    }
}

export function TechIdentityProvider({ children }) {
    // Gate on the MSAL account, not on roles. Gating on roles.length meant a
    // signed-in user with no app roles assigned never ran this effect, so
    // loading stayed true forever and anything waiting on it span rather than
    // showing an empty state. A missing role assignment should look like "no
    // data", not like a hang.
    const { accounts } = useMsal();
    const [techWebId, setTechWebId] = useState(null);
    const [loading, setLoading] = useState(true);

    // Read the real role off the token, never the effective one. useRoles()
    // reports the impersonated role, so asking it here would let an admin
    // impersonate a tech and then be unable to switch back.
    const canImpersonate = hasRole(accounts, "Admin");

    const [impersonated, setImpersonated] = useState(() => readStored());

    useEffect(() => {
        if (!accounts.length) return;       // MSAL has not resolved an account yet
        getMyTechIdentity()
        .then(r => setTechWebId(r?.techWebId || null))
        .finally(() => setLoading(false));
    }, [accounts.length]);

    // A stored impersonation from a previous page load is only honoured for an
    // admin. Without this, a leftover sessionStorage entry would keep scoping
    // the view for someone whose Admin role was removed in between.
    useEffect(() => {
        if (canImpersonate || !impersonated) return;
        setImpersonated(null);
        try { sessionStorage.removeItem(IMP_KEY); } catch { /* ignore */ }
    }, [canImpersonate, impersonated]);

    // who: a tech ({techWebId}) or a reader ({readerId}). kind is explicit
    // rather than inferred, so a record is never ambiguous about which view it
    // asked for.
    const startImpersonation = useCallback((who, kind = "tech") => {
        if (!canImpersonate || !who) return;
        let v = null;
        if (kind === "tech" && who.techWebId) {
            v = { kind: "tech", techWebId: String(who.techWebId),
                  name: who.name || "", email: who.email || "" };
        } else if (kind === "ip" && who.readerId) {
            v = { kind: "ip", readerId: String(who.readerId),
                  name: who.name || "", email: who.email || "" };
        }
        if (!v) return;
        setImpersonated(v);
        try { sessionStorage.setItem(IMP_KEY, JSON.stringify(v)); } catch { /* ignore */ }
    }, [canImpersonate]);

    const stopImpersonation = useCallback(() => {
        setImpersonated(null);
        try { sessionStorage.removeItem(IMP_KEY); } catch { /* ignore */ }
    }, []);

    const viewingAsTech = Boolean(canImpersonate && impersonated?.kind === "tech" && impersonated?.techWebId);
    const viewingAsIP   = Boolean(canImpersonate && impersonated?.kind === "ip" && impersonated?.readerId);
    const effectiveWebId = viewingAsTech ? impersonated.techWebId : techWebId;
    // Read by the API layer, which sends it so the server scopes the data. A
    // tech view is filtered in the browser; an IP view is filtered in the
    // query, so without this the page would show the physician's chrome over
    // an admin's data.
    const effectiveReaderId = viewingAsIP ? impersonated.readerId : null;

    return (
        <Ctx.Provider value={{
            techWebId, loading, impersonated, effectiveWebId, viewingAsTech,
            viewingAsIP, effectiveReaderId,
            canImpersonate, startImpersonation, stopImpersonation,
        }}>
            {children}
        </Ctx.Provider>
    );
}

export const useTechIdentity = () => useContext(Ctx);
